import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Mail, RefreshCw, Send, CheckCircle2, AlertTriangle, Clock, ArrowLeft, ExternalLink } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

const StatusPill = ({ campaign, currentWeek }) => {
  if (campaign.last_digest_error) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5" data-testid="digest-status-error">
      <AlertTriangle className="w-3 h-3" /> Failed
    </span>;
  }
  if (!campaign.last_digest_at) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-neutral-500 bg-neutral-50 border border-neutral-200 px-2 py-0.5">
      <Clock className="w-3 h-3" /> Never sent
    </span>;
  }
  if (campaign.last_digest_week === currentWeek) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5" data-testid="digest-status-current">
      <CheckCircle2 className="w-3 h-3" /> This week
    </span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5">
    <Clock className="w-3 h-3" /> Stale
  </span>;
};

export default function ReactivationDigests() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [batchBusy, setBatchBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/reactivation/digests/history");
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = data?.campaigns || [];
  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "failed") return rows.filter((c) => c.last_digest_error);
    if (filter === "never") return rows.filter((c) => !c.last_digest_at);
    if (filter === "current") return rows.filter((c) => c.last_digest_week === data?.current_week && !c.last_digest_error);
    if (filter === "stale") return rows.filter((c) => c.last_digest_at && c.last_digest_week !== data?.current_week);
    return rows;
  }, [rows, filter, data?.current_week]);

  const sendOne = async (campaignId) => {
    setBusyId(campaignId);
    try {
      const { data: res } = await api.post(`/reactivation/campaigns/${campaignId}/send-digest`);
      if (res.campaign?.last_digest_error) {
        toast.error(`Send failed: ${res.campaign.last_digest_error.slice(0, 200)}`);
      } else if (res.campaign?.last_digest_at) {
        toast.success(`Digest sent to ${res.campaign.last_digest_to}`);
      } else {
        toast.info("Send attempted — no email on file for this client.");
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(""); }
  };

  const sendBatch = async () => {
    if (!window.confirm("Force-resend digests for ALL active campaigns now? This overrides the once-per-week idempotency guard.")) return;
    setBatchBusy(true);
    try {
      const { data: res } = await api.post("/reactivation/digests/send-now?force=true");
      toast.success(`Batch complete: ${res.sent} sent · ${res.skipped} skipped · ${res.errors?.length || 0} errored`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBatchBusy(false); }
  };

  if (loading) return <div className="max-w-6xl mx-auto p-8 text-neutral-500">Loading digest history…</div>;

  const kpis = [
    { label: "Sent this week",  value: data?.sent_this_week || 0,  tone: "emerald" },
    { label: "Failed",          value: data?.errored || 0,         tone: "rose" },
    { label: "Never sent",      value: data?.never_sent || 0,      tone: "neutral" },
    { label: "Total campaigns", value: data?.total_campaigns || 0, tone: "blue" },
  ];

  const filters = [
    { key: "all",     label: "All",     count: rows.length },
    { key: "current", label: "This week", count: rows.filter((c) => c.last_digest_week === data?.current_week && !c.last_digest_error).length },
    { key: "stale",   label: "Stale",   count: rows.filter((c) => c.last_digest_at && c.last_digest_week !== data?.current_week).length },
    { key: "failed",  label: "Failed",  count: rows.filter((c) => c.last_digest_error).length },
    { key: "never",   label: "Never",   count: rows.filter((c) => !c.last_digest_at).length },
  ];

  return (
    <div className="max-w-6xl mx-auto p-8" data-testid="reactivation-digests-page">
      <Toaster position="top-right" richColors />
      <div className="mb-6">
        <Link to="/reactivation" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-black mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Reactivation
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline mb-2">Reactivation · Weekly digest</div>
            <h1 className="font-display text-4xl font-black tracking-tighter mb-2 inline-flex items-center gap-3">
              <Mail className="w-7 h-7" /> Digest history
            </h1>
            <p className="text-neutral-500 max-w-2xl">
              Every reactivation campaign, with its weekly-email audit trail. Re-send failures with one click.
              Automatic run: Mondays 8 AM UTC. Current week: <span className="font-mono font-semibold text-neutral-800">{data?.current_week}</span>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="lf-btn-outline text-sm inline-flex items-center gap-2" data-testid="digest-refresh">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={sendBatch}
              disabled={batchBusy}
              className="lf-btn-primary text-sm inline-flex items-center gap-2"
              data-testid="digest-send-batch"
            >
              <Send className="w-4 h-4" /> {batchBusy ? "Sending…" : "Force-send all"}
            </button>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {kpis.map((k) => (
          <div key={k.label} className={`border p-3 ${
            k.tone === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-900" :
            k.tone === "rose"    ? "bg-rose-50 border-rose-200 text-rose-900" :
            k.tone === "blue"    ? "bg-blue-50 border-blue-200 text-blue-900" :
                                   "bg-neutral-50 border-neutral-200 text-neutral-900"
          }`}>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{k.label}</div>
            <div className="text-2xl font-bold mt-0.5">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border transition ${
              filter === f.key
                ? "border-neutral-900 bg-neutral-900 text-white"
                : "border-neutral-200 hover:border-neutral-500 text-neutral-700"
            }`}
            data-testid={`digest-filter-${f.key}`}
          >
            {f.label} · {f.count}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="border border-neutral-200 bg-white overflow-x-auto" data-testid="digest-table">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
            <tr>
              <th className="text-left px-3 py-2">Campaign · Client</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Last sent</th>
              <th className="text-left px-3 py-2">Recipient</th>
              <th className="text-left px-3 py-2">Last stats</th>
              <th className="text-right px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-neutral-500 text-sm">No campaigns match this filter.</td></tr>
            )}
            {filtered.map((c) => {
              const stats = c.last_digest_stats || {};
              const compact = c.last_digest_at
                ? `${stats.appointments_booked ?? 0} booked · ${stats.leads_responded ?? 0} replied · ${stats.leads_attempted ?? 0} reached`
                : "—";
              return (
                <tr key={c.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`digest-row-${c.id.slice(0,8)}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-neutral-900 truncate max-w-xs">{c.name}</div>
                    <div className="text-xs text-neutral-500 truncate max-w-xs">{c.client_business_name || "—"}</div>
                  </td>
                  <td className="px-3 py-2.5"><StatusPill campaign={c} currentWeek={data?.current_week} /></td>
                  <td className="px-3 py-2.5 text-neutral-700">{fmt(c.last_digest_at)}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-neutral-600">{c.last_digest_to || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-neutral-700">
                    <div>{compact}</div>
                    {c.last_digest_error && (
                      <div className="text-[11px] text-rose-700 mt-1 max-w-xs truncate" title={c.last_digest_error}>
                        {c.last_digest_error}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      {c.public_token && (
                        <a
                          href={`/reactivation-portal/${c.public_token}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Preview client portal"
                          className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"
                          data-testid={`digest-portal-${c.id.slice(0,8)}`}
                        >
                          <ExternalLink className="w-3 h-3" /> Portal
                        </a>
                      )}
                      <button
                        onClick={() => sendOne(c.id)}
                        disabled={busyId === c.id}
                        className="text-xs border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-40"
                        data-testid={`digest-resend-${c.id.slice(0,8)}`}
                      >
                        <Send className="w-3 h-3" /> {busyId === c.id ? "Sending…" : "Send now"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
